import { escapeSql } from '@rdrive/helper-sql';
import { makeChartConfig } from '@rdrive/helper-chart-config';

// Issue distribution by creating team, drilling into the team's users, for
// @rdrive/plugin-chart-donut's BaseDonutChart with its opt-in `drill` level (IA_ABSORPTION_PLAN
// Phase 4 step 12b; was components/charts/CreatedByDonutChart.vue, whose query strings these are —
// the user query and both filter queries now escapeSql() their values, rule 6). Rendered `bare` in
// the Analysis page's Controller / Created By card:
//
//   :dataProvider="createdByDonut.getTeamDistribution"   :onSelect="createdByDonut.setTeamFilter"
//   :drill="{ dataProvider: createdByDonut.getUserDistribution,
//             onSelect: createdByDonut.setUserFilter, seriesName: $t('userDistribution') }"
//
// A team click shows the team's users and filters by the team; a user click filters by the user
// (replacing the team filter — one registration under `chartName`); the selected user again, or a
// slice without a user id, goes back to the teams and clears the filter (the base's drill rules).
export function useCreatedByDonutChart({ chartName, filterContext, conn }) {
  const { rows: run, applyFilter, removeFilter } = makeChartConfig({ chartName, filterContext, conn });

  // Top level. Rows: { teamId, name (may be null — the base labels it), value }
  const getTeamDistribution = async () => {
    const rows = await run(`
    SELECT 
      i.issueCreatedByTeamId AS teamId,
      t."Name" AS name,
      COUNT(*) AS value
    FROM filteredIssues i
    LEFT JOIN teams t ON i.issueCreatedByTeamId = t.Id
    GROUP BY i.issueCreatedByTeamId, t."Name"
    ORDER BY value DESC
  `);

    return rows.map((row) => ({ ...row, value: Number(row.value) }));
  };

  // Drill level: the users of one creating team. Rows: { teamId, name, value } — `teamId` is the
  // slice key (here the user id, handed back to setUserFilter); the unknown-user label is set here
  // because the base's own fallback says "Unknown Team".
  const getUserDistribution = async (teamId) => {
    const rows = await run(`
    SELECT 
      i.issueCreatedByUserId AS userId,
      u.DisplayName AS name,
      COUNT(*) AS value
    FROM filteredIssues i
    LEFT JOIN users u ON i.issueCreatedByUserId = u.Id
    WHERE i.issueCreatedByTeamId = '${escapeSql(teamId)}'
    GROUP BY i.issueCreatedByUserId, u.DisplayName
    ORDER BY value DESC
  `);

    return rows.map((row) => ({
      teamId: row.userId,
      name: row.name || `Unknown User (${row.userId})`,
      value: Number(row.value),
    }));
  };

  // teamId === null clears this chart's filter registration (both levels share it)
  const setTeamFilter = async (teamId) => {
    if (!teamId) {
      await removeFilter();
      return;
    }
    await applyFilter(`
      SELECT DISTINCT i.Id as id
      FROM issues i
      WHERE i.IssueCreatedByTeamId = '${escapeSql(teamId)}'
    `);
  };

  // A user of the drilled team (every issue that user created, as before — not only the team's)
  const setUserFilter = async (userId) => {
    await applyFilter(`
      SELECT DISTINCT i.Id as id
      FROM issues i
      WHERE i.IssueCreatedByUserId = '${escapeSql(userId)}'
    `);
  };

  return { getTeamDistribution, setTeamFilter, getUserDistribution, setUserFilter };
}
